import Button from "@mui/material/Button";
import { Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslate } from "../../../../../hooks/useTranslate";
import AgreementBox from "../AgreementBox";
import BaseDialog from "../../../../../components/BaseDialog";
import axiosManager from "../../../../../services/axiosManager";
import SnackbarToast from "../../../../../components/SnackbarToast";
import useStatuses from "../../../../../context/status/useStatuses";
import Preloader from "../../../../../components/Preloader";

const Agreements = (props) => {
    const {
        primarySignee, secondarySignee, studentId, onComplete,
    } = props;

    const { t } = useTranslate();
    const { generalStatuses } = useStatuses();

    const [agreementSelected, setAgreementSelected] = useState("Annual");
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);

    const issueBody = {
        ContractType: agreementSelected,
        StudentId: studentId,
        PrimaryContact: {
            ContactId: primarySignee.id,
            Name: primarySignee.name,
            EmailAddress: primarySignee.emailAddress,
        },
        SecondaryContact: secondarySignee ? {
            ContactId: secondarySignee.id,
            Name: secondarySignee.name,
            EmailAddress: secondarySignee.emailAddress,
        } : undefined,
    };

    const {
        isLoading: isLoadingOptions, data: agreementOptions,
    } = useQuery({
        queryKey: [`student/${studentId}/agreement-options`],
        queryFn: () => axiosManager.get$(`/parent/api/students/${studentId}/agreement-options`).then((response) => response?.data?.availableOptions),
        retry: 0,
        cacheTime: 0,
        keepPreviousData: true,
        enabled: !!studentId,
    });

    const {
        mutateAsync: issueAgreement, reset: resetIssueAgreement,
        isPending: isIssuingAgreement, isSuccess: isSuccessIssueAgreement, error: errorIssueAgreement,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/Students/${studentId}/Contracts?voidExisting=true`, issueBody),
    });

    const onProceedIssue = async () => {
        try {
            await issueAgreement();
            onComplete();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Preloader isLoading={isLoadingOptions}>
            <div className="mt-4 mb-12 flex flex-wrap align-stretch gap-8">
                {(agreementOptions || []).includes("Rolling") && (
                    <AgreementBox
                        isRecommended
                        title={t("enrolmentAgreement.annualAgreement.titleLength")}
                        isSelected={agreementSelected === "Annual"}
                        onClick={() => setAgreementSelected("Annual")}
                    >
                        {t("enrolmentAgreement.annualAgreement.point1")}
                    </AgreementBox>
                )}
                {(!generalStatuses.student?.studyingIb && (agreementOptions || []).includes("Rolling")) && (
                    <AgreementBox
                        title={t("enrolmentAgreement.perSemesterAgreement.titleLength")}
                        isSelected={agreementSelected === "Rolling"}
                        onClick={() => setAgreementSelected("Rolling")}
                    >
                        {t("enrolmentAgreement.perSemesterAgreement.point1")}
                    </AgreementBox>
                )}
            </div>
            <div className="mt-12 mb-12 flex justify-end">
                <Button
                    variant="contained"
                    className="button-accent"
                    onClick={() => setIssueDialogOpen(true)}
                    loading={isIssuingAgreement}
                >
                    {t("enrolmentAgreement.issueAgreement")}
                </Button>
            </div>
            <BaseDialog
                open={issueDialogOpen}
                onClose={() => setIssueDialogOpen(false)}
                withCloseButton
                isWide
            >
                <Typography variant="h2" className="text-primary" component="div">
                    {t("enrolmentAgreement.issueAgreementTitle")}
                </Typography>
                <Typography variant="body1" component="div" className="mt-4">
                    {t("enrolmentAgreement.issueAgreementDescription")}
                </Typography>
                <div className="flex align-center gap-8 mt-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={() => setIssueDialogOpen(false)}
                        loading={isIssuingAgreement}
                    >
                        {t("button.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        onClick={onProceedIssue}
                        loading={isIssuingAgreement}
                    >
                        {t("button.proceed")}
                    </Button>
                </div>
            </BaseDialog>
            <SnackbarToast
                open={isSuccessIssueAgreement || !!errorIssueAgreement}
                isSuccess={isSuccessIssueAgreement}
                isError={errorIssueAgreement}
                onClose={resetIssueAgreement}
            >
                {isSuccessIssueAgreement
                    ? t("enrolmentAgreement.successIssueAgreement")
                    : `${t("enrolmentAgreement.errorIssueAgreement")} (status ${errorIssueAgreement?.response?.status})`}
            </SnackbarToast>
        </Preloader>
    );
};

export default Agreements;
